import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Card,
  Form,
  Button,
  Spinner,
  Alert,
  Row,
  Col,
  Badge,
  Modal,
  Carousel,
} from "react-bootstrap";
import {
  FaShoppingBag,
  FaMapMarkerAlt,
  FaRupeeSign,
  FaEnvelope,
  FaPhone,
  FaComments,
  FaCrown,
  FaStar,
  FaThLarge,
  FaList,
  FaImage,
  FaTag,
} from "react-icons/fa";
import { motion } from "framer-motion";
import api from "../api";
import { AuthContext } from "../context/AuthContext";

export default function MarketplaceListings() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const createEmptyEditForm = () => ({
    name: "",
    description: "",
    category: "",
    price: "",
    type: "rent",
    city: "",
    state: "",
    contactEmail: "",
    contactPhone: "",
    imagesText: "",
  });
  const [query, setQuery] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [purchaseError, setPurchaseError] = useState("");
  const [purchaseNote, setPurchaseNote] = useState("");
  const [purchaseSubmitting, setPurchaseSubmitting] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [deleteError, setDeleteError] = useState("");
  const [deletingId, setDeletingId] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editForm, setEditForm] = useState(createEmptyEditForm);
  const [editError, setEditError] = useState("");
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editImageFiles, setEditImageFiles] = useState([]);
  const [editImagePreviews, setEditImagePreviews] = useState([]);
  const editFileInputRef = useRef(null);
  const [startingChatId, setStartingChatId] = useState("");
  const [recommendedItems, setRecommendedItems] = useState([]);
  const [recLoading, setRecLoading] = useState(false);
  const [recStrategy, setRecStrategy] = useState("trending");
  const [activeCategory, setActiveCategory] = useState("All");

  const userId = user?._id || user?.id || "";
  const apiBase = (api.defaults.baseURL || "").replace(/\/api$/, "");

  const resolveImageUrl = (src) => {
    if (!src) return "";
    if (/^(https?:)?\/\//i.test(src) || src.startsWith("data:")) {
      return src;
    }
    const prefix = apiBase.endsWith("/") || src.startsWith("/") ? "" : "/";
    return `${apiBase}${prefix}${src}`;
  };

  const loadItems = async (searchTerm = "") => {
    setLoading(true);
    setError("");
    setDeleteError("");
    setEditError("");
    try {
      const params = {};
      if (searchTerm) {
        params.q = searchTerm;
      }
      if (userId) {
        params.viewerId = userId;
      }
      const res = await api.get("/items", {
        params,
      });
      setItems(res.data?.items || []);
    } catch (err) {
      console.error("Marketplace load error:", err);
      setItems([]);
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Failed to load marketplace items."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, [userId]);

  useEffect(() => {
    let cancelled = false;
    const loadRecommendations = async () => {
      setRecLoading(true);
      try {
        const params = { limit: 12 };
        if (userId) {
          params.viewerId = userId;
        }
        const res = await api.get("/items/recommendations", { params });
        if (!cancelled) {
          setRecommendedItems(res.data?.items || []);
          setRecStrategy(res.data?.strategy || "trending");
        }
      } catch (err) {
        console.error("Recommendations load error:", err);
        if (!cancelled) {
          setRecommendedItems([]);
        }
      } finally {
        if (!cancelled) {
          setRecLoading(false);
        }
      }
    };
    loadRecommendations();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const sortedItems = useMemo(() => {
    const cloned = Array.isArray(items) ? [...items] : [];
    if (cloned.length === 0) {
      return cloned;
    }

    const normalizeForComparison = (value) =>
      typeof value === "string" ? value.trim().toLowerCase() : "";

    const userCity = normalizeForComparison(user?.city);
    const userState = normalizeForComparison(user?.state);

    const isPrimeSeller = (item) =>
      Boolean(item?.sellerPrime || item?.createdBy?.isPrime);

    const isSameCityAsUser = (item) => {
      if (!userCity) {
        return false;
      }
      const itemCity = normalizeForComparison(item?.city);
      if (!itemCity || itemCity !== userCity) {
        return false;
      }
      if (!userState) {
        return true;
      }
      const itemState = normalizeForComparison(item?.state);
      return !itemState || itemState === userState;
    };

    const isSameStateAsUser = (item) => {
      if (!userState) {
        return false;
      }
      const itemState = normalizeForComparison(item?.state);
      return itemState === userState;
    };

    const getGroupPriority = (item) => {
      const sameCity = isSameCityAsUser(item);
      const prime = isPrimeSeller(item);
      if (sameCity && prime) return 0;
      if (sameCity && !prime) return 1;
      if (!sameCity && prime) return 2;
      return 3;
    };

    const getStatePriority = (item) => (isSameStateAsUser(item) ? 0 : 1);

    return cloned.sort((a, b) => {
      const groupDiff = getGroupPriority(a) - getGroupPriority(b);
      if (groupDiff !== 0) {
        return groupDiff;
      }

      const stateDiff = getStatePriority(a) - getStatePriority(b);
      if (stateDiff !== 0) {
        return stateDiff;
      }

      const aCreated = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bCreated = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
      return bCreated - aCreated;
    });
  }, [items, user?.city, user?.state]);

  const recommendedIdSet = useMemo(
    () => new Set((recommendedItems || []).map((i) => String(i._id))),
    [recommendedItems]
  );

  const categories = useMemo(() => {
    const cats = new Set(items.map((i) => i.category?.trim()).filter(Boolean));
    return ["All", ...Array.from(cats).sort()];
  }, [items]);

  const gridItems = useMemo(() => {
    let filtered = sortedItems;
    if (activeCategory !== "All") {
      filtered = filtered.filter(
        (i) => (i.category || "").trim().toLowerCase() === activeCategory.toLowerCase()
      );
    }
    return filtered;
  }, [sortedItems, activeCategory]);

  const recommendationsHeading =
    recStrategy === "personalized"
      ? "Recommended for you"
      : recStrategy === "trending_geo"
      ? "Picked near you"
      : "Trending now";

  useEffect(() => {
    const previews = editImageFiles.map((file) => ({
      name: file.name,
      url: URL.createObjectURL(file),
    }));

    setEditImagePreviews((prev) => {
      prev.forEach((preview) => URL.revokeObjectURL(preview.url));
      return previews;
    });

    return () => {
      previews.forEach((preview) => URL.revokeObjectURL(preview.url));
    };
  }, [editImageFiles]);

  const handleSearch = (event) => {
    event.preventDefault();
    setDeleteError("");
    setSuccess("");
    setEditError("");
    loadItems(query.trim());
  };

  const openPurchaseModal = (item) => {
    setSelectedItem(item);
    setPurchaseNote("");
    setPurchaseError("");
    setShowPurchaseModal(true);
  };

  const closePurchaseModal = (force = false) => {
    if (purchaseSubmitting && !force) return;
    setShowPurchaseModal(false);
    setSelectedItem(null);
    setPurchaseNote("");
    setPurchaseError("");
  };

  const handlePurchase = async () => {
    if (!selectedItem || !user?.email) {
      setPurchaseError("You must be logged in to purchase an item.");
      return;
    }

    setPurchaseSubmitting(true);
    setPurchaseError("");
    try {
      const res = await api.post(`/items/${selectedItem._id}/purchase`, {
        buyerEmail: user.email,
        contactPhone: user.phone || undefined,
        note: purchaseNote.trim() || undefined,
      });

      if (res.data?.success) {
        setSuccess(
          "Purchase request sent to the seller. They will contact you soon."
        );
        closePurchaseModal(true);
      } else {
        setPurchaseError(
          res.data?.message || "Failed to submit purchase request."
        );
      }
    } catch (err) {
      console.error("Purchase request error:", err);
      setPurchaseError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Failed to submit purchase request."
      );
    } finally {
      setPurchaseSubmitting(false);
    }
  };

  const handleStartChat = async (item) => {
    if (!item || !item._id) {
      return;
    }
    if (!userId) {
      setError("You must be logged in to message the seller.");
      return;
    }

    setError("");
    setSuccess("");
    setStartingChatId(item._id);

    try {
      const res = await api.post("/chats", {
        itemId: item._id,
        buyerId: userId,
      });

      const conversationId = res.data?.conversation?._id;
      if (conversationId) {
        navigate(`/chats?conversationId=${conversationId}`);
      } else {
        setError(res.data?.message || "Unable to start chat with the seller.");
      }
    } catch (err) {
      console.error("Start chat error:", err);
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Unable to start chat with the seller."
      );
    } finally {
      setStartingChatId("");
    }
  };

  const handleDelete = async (item) => {
    if (!user?.email) {
      setDeleteError("You must be logged in to remove your listing.");
      return;
    }

    const confirmed = window.confirm(
      `Remove "${item.name}" from the marketplace?`
    );
    if (!confirmed) {
      return;
    }

    setDeleteError("");
    setSuccess("");
    setEditError("");
    setDeletingId(item._id);
    try {
      const res = await api.delete(`/items/${item._id}`, {
        data: { ownerEmail: user.email },
      });

      if (res.data?.success) {
        setItems((prev) =>
          prev.filter((listing) => String(listing._id) !== String(item._id))
        );
        setSuccess("Listing removed successfully.");
        if (editingItem && String(editingItem._id) === String(item._id)) {
          closeEditModal(true);
        }
      } else {
        throw new Error(res.data?.message || "Failed to remove listing.");
      }
    } catch (err) {
      console.error("Marketplace delete error:", err);
      setDeleteError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          "Failed to remove listing."
      );
    } finally {
      setDeletingId("");
    }
  };

  const handleEditFieldChange = (event) => {
    const { name, value } = event.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditFileChange = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length > 6) {
      setEditError("You can upload up to 6 new images per update.");
      setEditImageFiles(files.slice(0, 6));
    } else {
      setEditError((prev) =>
        prev === "You can upload up to 6 new images per update." ? "" : prev
      );
      setEditImageFiles(files);
    }
  };

  const clearEditFiles = () => {
    setEditImageFiles([]);
    if (editFileInputRef.current) {
      editFileInputRef.current.value = "";
    }
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setEditForm({
      name: item.name || "",
      description: item.description || "",
      category: item.category || "",
      price:
        item.price !== undefined && item.price !== null
          ? String(item.price)
          : "",
      type: item.type || "rent",
      city: item.city || "",
      state: item.state || "",
      contactEmail: item.contactEmail || "",
      contactPhone: item.contactPhone || "",
      imagesText: Array.isArray(item.images) ? item.images.join("\n") : "",
    });
    clearEditFiles();
    setEditError("");
    setDeleteError("");
    setSuccess("");
    setShowEditModal(true);
  };

  const closeEditModal = (force = false) => {
    if (editSubmitting && !force) return;
    setShowEditModal(false);
    setEditingItem(null);
    setEditError("");
    setEditForm(createEmptyEditForm());
    clearEditFiles();
  };

  const handleEditSubmit = async (event) => {
    event.preventDefault();
    if (!editingItem) {
      setEditError("No listing selected for editing.");
      return;
    }
    if (!user?.email) {
      setEditError("You must be logged in to edit your listing.");
      return;
    }

    const trimmedName = editForm.name.trim();
    const trimmedDescription = editForm.description.trim();

    if (!trimmedName || !trimmedDescription) {
      setEditError("Name and description are required.");
      return;
    }

    if (!["rent", "sale"].includes(editForm.type)) {
      setEditError("Listing type must be either rent or sale.");
      return;
    }

    if (editForm.price !== "") {
      const numericPrice = Number(editForm.price);
      if (Number.isNaN(numericPrice) || numericPrice < 0) {
        setEditError("Price must be a non-negative number.");
        return;
      }
    }

    setEditError("");
    setEditSubmitting(true);

    const manualImages = editForm.imagesText
      .split(/[\n,]/)
      .map((value) => value.trim())
      .filter(Boolean);

    let uploadedPaths = [];
    if (editImageFiles.length > 0) {
      const uploadData = new FormData();
      editImageFiles.forEach((file) => uploadData.append("images", file));
      try {
        const uploadRes = await api.post("/items/upload-images", uploadData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        if (uploadRes.data?.success) {
          uploadedPaths = uploadRes.data.images || [];
        } else {
          throw new Error(uploadRes.data?.message || "Image upload failed.");
        }
      } catch (uploadErr) {
        console.error("Image upload error:", uploadErr);
        setEditError(
          uploadErr.response?.data?.message ||
            uploadErr.message ||
            "Failed to upload images."
        );
        setEditSubmitting(false);
        return;
      }
    }

    const imageList = [...manualImages, ...uploadedPaths];
    const numericPrice =
      editForm.price !== "" ? Number(editForm.price) : undefined;

    const payload = {
      ownerEmail: user.email,
      name: trimmedName,
      description: trimmedDescription,
      category: editForm.category.trim() || undefined,
      price: numericPrice,
      type: editForm.type,
      state: editForm.state.trim() || undefined,
      city: editForm.city.trim() || undefined,
      contactEmail: editForm.contactEmail.trim() || undefined,
      contactPhone: editForm.contactPhone.trim() || undefined,
      images: imageList,
    };

    try {
      const res = await api.patch(`/items/${editingItem._id}`, payload);
      if (res.data?.success && res.data?.item) {
        const updatedItem = res.data.item;
        setItems((prev) =>
          prev.map((listing) =>
            String(listing._id) === String(updatedItem._id)
              ? { ...listing, ...updatedItem }
              : listing
          )
        );
        setSuccess(res.data.message || "Listing updated successfully.");
        closeEditModal(true);
      } else {
        setEditError(res.data?.message || "Failed to update listing.");
      }
    } catch (err) {
      console.error("Listing update error:", err);
      setEditError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          "Failed to update listing."
      );
    } finally {
      setEditSubmitting(false);
    }
  };

  return (
    <Container className="py-4">
      {/* Sleek Hero Header */}
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div>
          <h2 className="fw-bold mb-1">Marketplace</h2>
          <p className="text-muted mb-0">Discover and rent items in your community</p>
        </div>
      </div>

      <Card className="shadow-sm border-0 rounded-4 mb-4">
        <Card.Body>
          <Form onSubmit={handleSearch}>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Search marketplace</Form.Label>
              <div className="d-flex gap-2">
                <Form.Control
                  type="search"
                  placeholder="Search by name, description, or category"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                />
                <Button type="submit" variant="primary" className="px-4">Search</Button>
              </div>
            </Form.Group>
            
            {/* Category Filter Chips */}
            <div className="d-flex flex-wrap gap-2 mt-3">
              {categories.map((cat) => (
                <button
                  key={cat}
                  type="button"
                  className={`category-chip ${activeCategory === cat ? "active" : ""}`}
                  onClick={() => setActiveCategory(cat)}
                >
                  {cat}
                </button>
              ))}
            </div>
          </Form>
        </Card.Body>
      </Card>

      {success && (
        <Alert
          variant="success"
          onClose={() => setSuccess("")}
          dismissible
          className="mb-4"
        >
          {success}
        </Alert>
      )}

      {deleteError && (
        <Alert
          variant="danger"
          onClose={() => setDeleteError("")}
          dismissible
          className="mb-4"
        >
          {deleteError}
        </Alert>
      )}

      {error && <Alert variant="warning">{error}</Alert>}


      {loading ? (
        <div className="text-center my-5">
          <Spinner animation="border" />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center my-5 text-muted">
          <FaShoppingBag size={56} className="mb-3" />
          <div className="fw-semibold">No listings found.</div>
          <div>Try another search term or add a new item.</div>
        </div>
      ) : (
        <Row className="g-4">
          {gridItems.map((item, index) => {
            const rawCreatedBy =
              (item?.createdBy && item.createdBy._id) || item?.createdBy || "";
            const ownerId = rawCreatedBy ? String(rawCreatedBy) : "";
            const isOwner =
              ownerId && userId ? ownerId === String(userId) : false;
            const chatLoading = startingChatId === item._id;
            const showPhone = isOwner && item.contactPhone;
            const showEmail = isOwner && item.contactEmail;
            const isPrimeSeller = Boolean(
              item?.sellerPrime || (item?.createdBy && item.createdBy.isPrime)
            );

            return (
              <Col key={item._id || index} xs={12} md={6} lg={4}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05, duration: 0.4 }}
                  className="h-100"
                >
                  <Card className="h-100 shadow-sm border-0 rounded-4 overflow-hidden">
                    {Array.isArray(item.images) && item.images.length > 0 ? (
                      <div className="position-relative overflow-hidden">
                        <Carousel
                          indicators={item.images.length > 1}
                          controls={item.images.length > 1}
                          interval={null}
                          className="h-100"
                        >
                          {item.images.map((imageSrc, imgIdx) => (
                            <Carousel.Item key={`${item._id || index}-${imgIdx}`}>
                              <img
                                src={resolveImageUrl(imageSrc)}
                                alt={`${item.name} image ${imgIdx + 1}`}
                                className="d-block w-100 img-zoom-hover"
                                style={{ height: 260, objectFit: "cover" }}
                              />
                            </Carousel.Item>
                          ))}
                        </Carousel>
                        {item.images.length > 1 && (
                          <Badge bg="secondary" className="position-absolute top-0 end-0 m-3 opacity-75 rounded-pill px-3 py-2 text-white border border-light">
                            <FaImage className="me-1" /> {item.images.length}
                          </Badge>
                        )}
                      </div>
                    ) : (
                      <div className="bg-light d-flex flex-column align-items-center justify-content-center text-muted" style={{ height: 260 }}>
                        <FaImage size={40} className="mb-2 opacity-50" />
                        <span className="small">No image available</span>
                      </div>
                    )}
                    
                    <div className="d-flex flex-column flex-grow-1 p-3">
                      <Card.Body className="p-0">
                        <div className="d-flex align-items-start justify-content-between mb-2">
                          <h4 className="mb-0 fw-bold text-dark d-flex flex-wrap align-items-center gap-2">
                            {item.name}
                            {isPrimeSeller && (
                              <Badge bg="warning" text="dark" className="rounded-pill px-3 py-1 fs-6">
                                <FaCrown className="me-1" /> Prime
                              </Badge>
                            )}
                          </h4>
                          <span className={`badge rounded-pill ${item.type === 'sale' ? 'bg-success' : 'bg-primary'} px-3 py-2 fs-6 text-capitalize`}>
                            {item.type || "rent"}
                          </span>
                        </div>
                        
                        <div className="d-flex align-items-center gap-3 mb-4 text-muted">
                          <span>
                            <FaMapMarkerAlt className="text-primary me-1" />
                            {item.city || ""}{item.city && item.state ? ", " : ""}{item.state || ""}
                          </span>
                          {item.category && (
                            <span>
                              <FaTag className="text-secondary me-1" />
                              {item.category}
                            </span>
                          )}
                        </div>

                        <Card.Text className="mb-3">
                          {item.price != null && (
                            <span className="fs-3 fw-bold text-dark mb-0 d-flex align-items-center gap-1">
                              <FaRupeeSign className="fs-4" /> {item.price}
                            </span>
                          )}
                        </Card.Text>
                        
                        <div className="mb-4 text-secondary" style={{ fontSize: '1.05rem', lineHeight: '1.5' }}>
                          {item.description}
                        </div>
                      </Card.Body>
                      
                      <Card.Footer className="bg-transparent border-top-0 p-0 mt-auto">
                        {isOwner ? (
                          <>
                            <div className="bg-light p-3 rounded-3 mb-3 d-flex flex-column gap-2">
                              <span className="fw-semibold text-dark">Your contact details:</span>
                              {showPhone ? (
                                <span className="text-secondary d-flex align-items-center gap-2">
                                  <FaPhone className="text-primary" /> {item.contactPhone}
                                </span>
                              ) : null}
                              {showEmail ? (
                                <span className="text-secondary d-flex align-items-center gap-2">
                                  <FaEnvelope className="text-primary" /> {item.contactEmail}
                                </span>
                              ) : null}
                            </div>
                            <div className="d-flex gap-2">
                              <Button variant="outline-primary" className="w-50 rounded-pill py-2" onClick={() => openEditModal(item)} disabled={(editingItem && editingItem._id === item._id && (showEditModal || editSubmitting)) || deletingId === item._id}>
                                Edit
                              </Button>
                              <Button variant="outline-danger" className="w-50 rounded-pill py-2" onClick={() => handleDelete(item)} disabled={deletingId === item._id}>
                                {deletingId === item._id ? <Spinner animation="border" size="sm" /> : "Remove"}
                              </Button>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="bg-light p-3 rounded-3 text-secondary mb-3 d-flex align-items-center">
                              <FaComments className="text-primary me-2 flex-shrink-0" />
                              Seller details unlock after you start a conversation.
                            </div>
                            <div className="d-flex gap-2">
                              <Button variant="outline-primary" className="w-50 rounded-pill py-2 d-flex justify-content-center align-items-center gap-2" onClick={() => handleStartChat(item)} disabled={chatLoading}>
                                {chatLoading ? (
                                  <>
                                    <Spinner animation="border" size="sm" /> Opening...
                                  </>
                                ) : (
                                  <>
                                    <FaComments /> Message Seller
                                  </>
                                )}
                              </Button>
                              {item.type === "sale" && (
                                <Button variant="primary" className="w-50 rounded-pill py-2 fw-semibold" onClick={() => openPurchaseModal(item)}>
                                  Buy Now
                                </Button>
                              )}
                            </div>
                          </>
                        )}
                      </Card.Footer>
                    </div>
                  </Card>
                </motion.div>
              </Col>
            );
          })}
        </Row>
      )}

      <Modal
        show={showPurchaseModal}
        onHide={() => closePurchaseModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Purchase {selectedItem ? selectedItem.name : "item"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {purchaseError && <Alert variant="danger">{purchaseError}</Alert>}
          <p className="text-muted mb-3">
            Confirm your interest in buying this item. The seller will reach out
            using your profile contact details.
          </p>
          {selectedItem && (
            <div className="mb-3">
              <div className="fw-semibold">Price: ₹{selectedItem.price}</div>
              <div className="text-secondary">
                Location: {selectedItem.city || ""}
                {selectedItem.city && selectedItem.state ? ", " : ""}
                {selectedItem.state || ""}
              </div>
            </div>
          )}
          <Form.Group className="mb-0">
            <Form.Label>Message for the seller (optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={purchaseNote}
              onChange={(event) => setPurchaseNote(event.target.value)}
              placeholder="Share any questions or delivery preferences"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closePurchaseModal}>
            Cancel
          </Button>
          <Button onClick={handlePurchase} disabled={purchaseSubmitting}>
            {purchaseSubmitting ? (
              <Spinner animation="border" size="sm" />
            ) : (
              "Confirm Purchase"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showEditModal}
        onHide={() => closeEditModal(false)}
        centered
        size="lg"
      >
        <Form onSubmit={handleEditSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>
              {editingItem ? `Edit ${editingItem.name}` : "Edit listing"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {editError && <Alert variant="danger">{editError}</Alert>}
            <Form.Group className="mb-3">
              <Form.Label>Item name</Form.Label>
              <Form.Control
                name="name"
                value={editForm.name}
                onChange={handleEditFieldChange}
                placeholder="Item title"
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={editForm.description}
                onChange={handleEditFieldChange}
                placeholder="Describe your item"
                required
              />
            </Form.Group>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Category</Form.Label>
                  <Form.Control
                    name="category"
                    value={editForm.category}
                    onChange={handleEditFieldChange}
                    placeholder="Category"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Listing type</Form.Label>
                  <Form.Select
                    name="type"
                    value={editForm.type}
                    onChange={handleEditFieldChange}
                  >
                    <option value="rent">For Rent</option>
                    <option value="sale">For Sale</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3 mt-3">
              <Form.Label>Price (₹)</Form.Label>
              <Form.Control
                type="number"
                min="0"
                step="1"
                name="price"
                value={editForm.price}
                onChange={handleEditFieldChange}
                placeholder="Enter price or leave blank to keep current"
              />
            </Form.Group>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>City</Form.Label>
                  <Form.Control
                    name="city"
                    value={editForm.city}
                    onChange={handleEditFieldChange}
                    placeholder="City"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>State</Form.Label>
                  <Form.Control
                    name="state"
                    value={editForm.state}
                    onChange={handleEditFieldChange}
                    placeholder="State"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="g-3 mt-1">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Contact email</Form.Label>
                  <Form.Control
                    type="email"
                    name="contactEmail"
                    value={editForm.contactEmail}
                    onChange={handleEditFieldChange}
                    placeholder="contact@example.com"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Contact phone</Form.Label>
                  <Form.Control
                    name="contactPhone"
                    value={editForm.contactPhone}
                    onChange={handleEditFieldChange}
                    placeholder="Phone number"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-4 mt-3">
              <Form.Label>Add new photos</Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                multiple
                onChange={handleEditFileChange}
                ref={editFileInputRef}
              />
              <Form.Text className="text-muted">
                Upload up to six additional images (PNG, JPG, or WebP, max 5MB
                each).
              </Form.Text>
              {editImagePreviews.length > 0 && (
                <Row className="g-3 mt-2">
                  {editImagePreviews.map((preview) => (
                    <Col xs={6} sm={4} key={preview.url}>
                      <div className="border rounded-3 p-2 h-100 d-flex flex-column align-items-center">
                        <img
                          src={preview.url}
                          alt={preview.name}
                          className="img-fluid rounded mb-2"
                          style={{
                            width: "100%",
                            maxHeight: 140,
                            objectFit: "cover",
                          }}
                        />
                        <div className="text-truncate small w-100 text-center">
                          {preview.name}
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
              )}
              {editImagePreviews.length > 0 && (
                <div className="mt-2">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={clearEditFiles}
                  >
                    Clear new uploads
                  </Button>
                </div>
              )}
            </Form.Group>
            <Form.Group className="mb-0">
              <Form.Label>Image URLs (one per line)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="imagesText"
                value={editForm.imagesText}
                onChange={handleEditFieldChange}
                placeholder="Existing and external image links"
              />
              <Form.Text className="text-muted">
                Remove a line to delete an existing photo or paste new public
                image URLs.
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => closeEditModal(false)}
              disabled={editSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={editSubmitting}>
              {editSubmitting ? (
                <Spinner animation="border" size="sm" />
              ) : (
                "Save changes"
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
}
